import { PayloadAction } from '@reduxjs/toolkit';
import { isEqual, omit, uniqBy } from 'lodash-es';

import { ICartEntry } from '@rbi-ctg/menu';
import { IOffersFragment } from 'generated/graphql-gateway';
import { SYNC_WITH_STORAGE_ACTION } from 'state/global-state/global-actions';
import { createAppSlice } from 'state/global-state/utils';
import { IncentiveEvaluationMap } from 'state/loyalty/hooks/types';
import { LoyaltyAppliedOffer, LoyaltyOffer } from 'state/loyalty/types';
import { isDiscountLoyaltyOffer } from 'state/loyalty/utils';

import { userSlice } from '../user/user.slice';
import { LoyaltyUser } from '../user/user.types';

import { IOffersState } from './offers.types';
import {
  getAppliedOffersFromStorage,
  getSelectedOfferFromStorage,
  removeSelectedOfferInStorage,
  updateAppliedOffersInStorage,
  updateSelectedOfferInStorage,
} from './offers.utils';

export const initialState: IOffersState = {
  appliedOffers: [],
  cmsOffers: [],
  incentivesIds: [],
  offers: [],
  offersFeedbackMap: {},
  offersLoading: false,
  offerRedemptionAvailableAfter: null,
  personalizedOffers: [],
  selectedOffer: null,
  userOffers: [],
  shouldRefetchOffers: true, // needed for initial load
  loyaltyUserId: undefined,
};

export const offersSlice = createAppSlice({
  name: 'offers',
  initialState,
  reducers: {
    applyOffer: (state, { payload: newOffer }: PayloadAction<LoyaltyAppliedOffer>) => {
      const isStackableOffer = newOffer.isStackable;
      const updatedOffers = state.appliedOffers
        .filter((appliedOffer: LoyaltyAppliedOffer) => {
          if (isStackableOffer) {
            // Ensures we are not adding the same offer
            return appliedOffer.id !== newOffer.id;
          }
          // filters out non-stackable offers
          return appliedOffer.isStackable;
        })
        .concat(newOffer);
      updateAppliedOffersInStorage(updatedOffers);
      state.appliedOffers = updatedOffers;
    },
    unshiftPersonalizedOffer: (state, { payload }: PayloadAction<LoyaltyOffer>) => {
      state.personalizedOffers = [payload, ...state.personalizedOffers];
    },
    resetAppliedOffers: state => {
      updateAppliedOffersInStorage([]);
      state.appliedOffers = [];
    },
    resetAppliedDiscountOffers: state => {
      const discountCmsOffersIds = new Set<string>();
      state.cmsOffers.forEach(cmsOffer => {
        if (cmsOffer.loyaltyEngineId && isDiscountLoyaltyOffer(cmsOffer)) {
          discountCmsOffersIds.add(cmsOffer.loyaltyEngineId);
        }
      });

      const updatedOffers = state.appliedOffers.filter(
        appliedOffer => !discountCmsOffersIds.has(appliedOffer.id || '')
      );
      updateAppliedOffersInStorage(updatedOffers);
      state.appliedOffers = updatedOffers;
    },
    resetOfferFeedbackMap: state => {
      state.offersFeedbackMap = {};
    },
    setAppliedOffers: (state, { payload }: PayloadAction<LoyaltyAppliedOffer[]>) => {
      updateAppliedOffersInStorage(payload);
      state.appliedOffers = payload;
    },
    setCmsOffers: (state, { payload }: PayloadAction<LoyaltyOffer[]>) => {
      state.cmsOffers = uniqBy([...payload, ...state.cmsOffers], '_id');
    },
    setIncentivesIds: (state, { payload }: PayloadAction<string[]>) => {
      state.incentivesIds = payload;
    },
    setOffers: (state, { payload }: PayloadAction<IOffersFragment[]>) => {
      state.offers = payload;
    },
    setOffersFeedbackMap: (state, { payload }: PayloadAction<IncentiveEvaluationMap>) => {
      state.offersFeedbackMap = payload;
    },
    setOffersLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.offersLoading = payload;
    },
    // TODO: remove this function and get the value from the user
    setOfferRedemptionAvailableAfter: (state, { payload }: PayloadAction<string | null>) => {
      state.offerRedemptionAvailableAfter = payload;
    },
    setPersonalizedOffers: (state, { payload }: PayloadAction<LoyaltyOffer[]>) => {
      state.personalizedOffers = payload;
    },
    setSelectedOffer: (state, { payload }: PayloadAction<LoyaltyOffer | null>) => {
      updateSelectedOfferInStorage(payload);
      state.selectedOffer = payload;
    },
    setUserOffers: (state, { payload }: PayloadAction<IOffersFragment[]>) => {
      state.userOffers = payload;
    },
    removeAppliedOffer: (state, { payload: appliedOffer }: PayloadAction<LoyaltyAppliedOffer>) => {
      const updatedOffers = state.appliedOffers.filter(offer => {
        return offer.cartId === appliedOffer.cartId;
      });

      updateAppliedOffersInStorage(updatedOffers);

      state.appliedOffers = updatedOffers;
    },
    removeAppliedOfferByCartEntry: (state, { payload: cartEntry }: PayloadAction<ICartEntry>) => {
      const updatedOffers = state.appliedOffers.filter(offer => {
        if (
          cartEntry.cartId === offer.cartId &&
          offer.id === state.selectedOffer?.loyaltyEngineId
        ) {
          removeSelectedOfferInStorage();
        }
        if (offer?.id && state.offersFeedbackMap[offer.id]) {
          state.offersFeedbackMap = omit(state.offersFeedbackMap, offer.id);
        }
        // If cart entry has children, cartIds could match
        return offer?.cartId !== cartEntry?.cartId;
      });

      updateAppliedOffersInStorage(updatedOffers);

      state.appliedOffers = updatedOffers;
    },
    setShouldRefetchOffers: (state, { payload }: PayloadAction<boolean>) => {
      state.shouldRefetchOffers = payload;
    },
  },
  extraReducers: {
    [SYNC_WITH_STORAGE_ACTION]: (state: IOffersState) => {
      const offersFromStorage = getAppliedOffersFromStorage();
      if (!isEqual(state.appliedOffers, offersFromStorage)) {
        state.appliedOffers = offersFromStorage;
      }
      state.selectedOffer = getSelectedOfferFromStorage();
    },
    [userSlice.actions.setUser.type]: (
      state: IOffersState,
      { payload: loyaltyUser }: PayloadAction<LoyaltyUser | null>
    ) => {
      // If loyalty user is null, reset user offers
      // This handles the logout case
      if (!loyaltyUser) {
        state.personalizedOffers = [];
        state.userOffers = [];
        state.offerRedemptionAvailableAfter = null;
      }

      // Update offers only when loyaltyUser changes
      if (state.loyaltyUserId !== loyaltyUser?.id) {
        state.shouldRefetchOffers = true;
      }

      // Cache the loyalty user id
      state.loyaltyUserId = loyaltyUser?.id;
    },
  },
});
